import React, { useMemo, useState } from 'react';
import { AlertTriangle, Clock, ListFilter, Menu, Star } from 'lucide-react';
import { TaskItem } from '../../../types/topic';
import MobileDashboardFilterDropdown from './MobileDashboardFilterDropdown';
import {
  ProjectFilter,
  TaskDeleteHandler,
  TaskSection,
  TaskUpdateHandler,
  UserFilter,
} from '../types/dashboardTypes';
import { animated } from 'react-spring';
import DashboardTaskColumn from '../DashboardTaskColumn';
import { DashboardSection, MobileDashboardColumnSwitcher } from './MobileDashboardColumnSwitcher';
import { useDashboardColumnSwipe } from '../hooks/useDashboardColumnSwipe';
import { useStatusBarInfo } from '../../../hooks/useStatusBarInfo';
import { logger } from '../../../utils/logger';
import { FILTER_CONSTANTS } from '../constants/filterConstants';

interface MobileDashboardViewProps {
  overdueTasks: TaskItem[];
  todayTasks: TaskItem[];
  thisWeekTasks: TaskItem[];
  highlightedTasks: TaskItem[];
  projects: Array<{ id: string; name: string }>;
  onEditTask: (task: TaskItem) => void;
  onUpdateTask: TaskUpdateHandler;
  onDeleteTask: TaskDeleteHandler;
  onNavigateToTask: (task: TaskItem) => void;
  setIsDrawerOpen: (open: boolean) => void;
  projectFilters: ProjectFilter[];
  userFilters: UserFilter[];
  toggleProjectFilter: (projectId: string, isExclusive?: boolean) => void;
  toggleUserFilter: (userId: string, isExclusive?: boolean) => void;
  availableTags?: string[];
  selectedTags?: string[];
  onTagsChange?: (tags: string[]) => void;
}

export function MobileDashboardView({
  overdueTasks,
  todayTasks,
  thisWeekTasks,
  highlightedTasks,
  projects,
  onEditTask,
  onUpdateTask,
  onDeleteTask,
  onNavigateToTask,
  setIsDrawerOpen,
  projectFilters,
  userFilters,
  toggleProjectFilter,
  toggleUserFilter,
  availableTags = [],
  selectedTags = [],
  onTagsChange = () => {},
}: MobileDashboardViewProps) {
  // Default to showing highlighted tasks
  const [activeSection, setActiveSection] = useState<DashboardSection>('highlighted');
  // Add ref to maintain consistent width
  const containerRef = React.useRef<HTMLDivElement>(null);
  // Filter dropdown state
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  // Scroll shadow state
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Check if any filters are active (using props instead of store)
  const hasActiveFilters =
    projectFilters.some(f => f.active) ||
    userFilters.some(f => f.active) ||
    selectedTags.length > 0 ||
    userFilters.some(f => f.id === FILTER_CONSTANTS.SHARED_UNASSIGNED && f.active);

  // Add scroll listener to detect when to show header shadow
  React.useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const scrollTop = containerRef.current.scrollTop;
        setIsScrolled(scrollTop > 10);
      }
    };

    const scrollContainer = containerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Create a memoized object for section counts to avoid unnecessary re-renders
  const sectionCounts = useMemo(() => ({
    overdue: overdueTasks?.length || 0,
    today: todayTasks?.length || 0,
    thisWeek: thisWeekTasks?.length || 0,
    highlighted: highlightedTasks?.length || 0,
  }), [overdueTasks?.length, todayTasks?.length, thisWeekTasks?.length, highlightedTasks?.length]);

  // Map sections to their data with memoization
  const sectionMap = useMemo(() => ({
    overdue: {
      tasks: overdueTasks,
      title: 'Overdue',
      icon: AlertTriangle,
      iconBgColor: 'bg-red-100',
      iconColor: 'text-red-500',
      sectionType: 'overdue' as TaskSection,
    },
    today: {
      tasks: todayTasks,
      title: 'Today',
      icon: Clock,
      iconBgColor: 'bg-green-100',
      iconColor: 'text-green-500',
      sectionType: 'today' as TaskSection,
    },
    thisWeek: {
      tasks: thisWeekTasks,
      title: 'This Week',
      icon: Clock,
      iconBgColor: 'bg-blue-100',
      iconColor: 'text-blue-500',
      sectionType: 'thisWeek' as TaskSection,
    },
    highlighted: {
      tasks: highlightedTasks,
      title: 'Highlighted',
      icon: Star,
      iconBgColor: 'bg-yellow-100',
      iconColor: 'text-yellow-500',
      sectionType: 'highlighted' as TaskSection,
    },
  }), [overdueTasks, todayTasks, thisWeekTasks, highlightedTasks]);

  const activeData = sectionMap[activeSection];

  // Get status bar information for mobile adjustments
  const { isNativeApp, statusBarHeight, navigationBarHeight } = useStatusBarInfo();

  // Log status bar info for debugging
  React.useEffect(() => {
    if (isNativeApp) {
      logger.capacitor('MobileDashboardView: Using status bar height', {
        statusBarHeight,
        navigationBarHeight,
      });
    }
  }, [isNativeApp, statusBarHeight, navigationBarHeight]);

  // Ordered section IDs for swipe navigation - highlighted first
  const orderedSectionIds: DashboardSection[] = ['highlighted', 'overdue', 'today', 'thisWeek'];

  // Set up swipe gesture handling
  const { bind: swipeBind, x } = useDashboardColumnSwipe({
    sectionIds: orderedSectionIds,
    activeSection: activeSection,
    onSectionChange: sectionId => {
      setActiveSection(sectionId);
    },
    onBackToTopics: () => setIsDrawerOpen(true),
    threshold: 0.15,
  });

  return (
    <div className="flex flex-col h-full">
      {/* Mobile header buttons - add shadow based on scroll state and status bar padding */}
      <div
        className={`fixed top-0 left-0 right-0 z-[40] bg-[var(--content-bg-color)] backdrop-blur-sm transition-shadow duration-200 ${isScrolled ? 'shadow-sm border-b border-gray-100' : ''}`}
        style={{
          paddingTop: isNativeApp ? `${statusBarHeight}px` : '0',
          height: isNativeApp ? `calc(3.5rem + ${statusBarHeight}px)` : '3.5rem',
        }}
      >
        {/* Header buttons container - properly positioned with status bar in mind */}
        <div
          className="w-full absolute left-0 right-0 flex items-center justify-between"
          style={{
            top: isNativeApp ? `${statusBarHeight}px` : '0',
            height: '3.5rem', // Match the height of the column switcher
          }}
        >
          {/* Menu button */}
          <div className="pl-4">
            <button
              onClick={() => setIsDrawerOpen(true)}
              className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
          </div>

          {/* Filter button */}
          <div className="pr-4">
            <button
              onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
              className={`p-2 transition-colors relative
                ${
                  hasActiveFilters
                    ? 'text-blue-600 hover:text-blue-700'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              aria-label="Filter dashboard"
            >
              <ListFilter size={20} />
              {hasActiveFilters && (
                <span className="absolute top-0 right-0 block h-2.5 w-2.5 rounded-full bg-blue-500 transform -translate-y-1 translate-x-1"></span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Column switcher */}
      <MobileDashboardColumnSwitcher
        activeSection={activeSection}
        onSectionSelect={setActiveSection}
        sectionCounts={sectionCounts}
      />

      {/* Main content - adjusted for status bar and navigation bar */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto px-2 w-full fixed inset-0 bg-[var(--content-bg-color)]"
        style={{
          paddingTop: isNativeApp
            ? `calc(3.5rem + ${statusBarHeight}px + 0.5rem)`
            : 'calc(3.5rem + 0.5rem)',
          paddingBottom: isNativeApp ? `${navigationBarHeight}px` : '1rem',
        }}
      >
        {/* Swipe detection and content container */}
        <animated.div
          {...swipeBind()}
          className="h-full"
          style={{
            height: '100%',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'pan-y',
            transform: x.to(x => `translateX(${x}px)`),
          }}
        >
          <div
            key={activeSection}
            className="w-full rounded-lg py-3 px-1 mb-4"
            style={{ minWidth: '100%' }}
          >
            <DashboardTaskColumn
              title={activeData.title}
              icon={activeData.icon}
              iconBgColor={activeData.iconBgColor}
              iconColor={activeData.iconColor}
              tasks={activeData.tasks}
              sectionType={activeData.sectionType}
              projects={projects}
              isMobile={true}
              onEditTask={onEditTask}
              onUpdateTask={onUpdateTask}
              onDeleteTask={onDeleteTask}
              onNavigateToTask={onNavigateToTask}
            />
          </div>
        </animated.div>
      </div>

      {/* Mobile dashboard filter dropdown */}
      <MobileDashboardFilterDropdown
        isOpen={isFilterDropdownOpen}
        onClose={() => setIsFilterDropdownOpen(false)}
        userFilters={userFilters}
        projectFilters={projectFilters.filter(f => f.hasTasks)}
        toggleUserFilter={toggleUserFilter}
        toggleProjectFilter={toggleProjectFilter}
        availableTags={availableTags}
        selectedTags={selectedTags}
        onTagsChange={onTagsChange}
      />
    </div>
  );
}
